import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLockup } from "../components/BrandLogo";
import { LEGAL, LEGAL_LINKS, OPERATOR, CONTACT_EMAIL, EFFECTIVE_DATE } from "../content/legal";

function Block({ item }) {
  if (typeof item === "string") {
    return <p className="text-sm sm:text-base text-body leading-relaxed mb-3">{item}</p>;
  }
  if (item.list) {
    return (
      <ul className="mb-4 space-y-2">
        {item.list.map((li, i) => (
          <li key={i} className="flex gap-2.5 text-sm sm:text-base text-body leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
            <span>{li}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function Legal({ type }) {
  const navigate = useNavigate();
  const key = type === "retention" ? "retention" : type;
  const doc = LEGAL[key];
  if (!doc) return null;

  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid={`legal-page-${doc.slug}`}>
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-surface/90 border-b border-line">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center group" aria-label="GetInterviewed home">
            <BrandLockup />
          </Link>
          <button
            data-testid="legal-back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 af-fade-up">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="legal-nav">
          {LEGAL_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`legal-link-${l.to.replace("/", "")}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                l.to === `/${doc.slug}`
                  ? "bg-brand-soft border-brand/40 text-brand"
                  : "border-line text-body hover:bg-subtle"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{doc.title}</h1>
        <p className="mt-2 text-sm text-faint font-mono2">
          {OPERATOR} · Last updated: {EFFECTIVE_DATE}
        </p>
        <p className="mt-6 text-sm sm:text-base text-body leading-relaxed border-l-2 border-brand/40 pl-4">
          {doc.intro}
        </p>

        <div className="mt-8 space-y-8">
          {doc.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-lg font-semibold text-ink mb-2">{s.heading}</h2>
              {s.body.map((b, j) => <Block key={j} item={b} />)}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-xl border border-line bg-surface p-5 text-sm text-body">
          Questions about this document? Contact{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">{CONTACT_EMAIL}</a>.
        </div>
      </main>

      <footer className="border-t border-line py-8">
        <div className="max-w-4xl mx-auto px-6 text-sm text-faint flex items-center justify-between flex-wrap gap-3">
          <span>© {new Date().getFullYear()} GetInterviewed</span>
          <span className="font-mono2 text-xs">Created by rukeshvaran</span>
        </div>
      </footer>
    </div>
  );
}
